<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';

const props = defineProps({ parties: { type: Array, default: () => [] } });
const { t } = useI18n();
const partyGroups = computed(() =>
  [
    { key: 'vendedor', label: t('SALES.DETAIL.SELLERS') },
    { key: 'comprador', label: t('SALES.DETAIL.BUYERS') },
  ].map(group => ({
    ...group,
    parties: props.parties.filter(party => party.party_role === group.key),
  }))
);
const identity = party => party.profile?.identity || {};
const contact = party => identity(party).phone || '';
const personSuffix = count =>
  count === 1
    ? t('SALES.DETAIL.SALE_SUMMARY.PERSON_SINGLE')
    : t('SALES.DETAIL.SALE_SUMMARY.PERSON_PLURAL');
const companySuffix = count =>
  count === 1
    ? t('SALES.DETAIL.SALE_SUMMARY.COMPANY_SINGLE')
    : t('SALES.DETAIL.SALE_SUMMARY.COMPANY_PLURAL');
const countLabel = (parties, kind) => {
  const count = parties.filter(party => party.party_kind === kind).length;
  if (!count) return '';

  const suffix =
    kind === 'pessoa_fisica' ? personSuffix(count) : companySuffix(count);
  return `${count} - ${suffix}`;
};
</script>

<template>
  <div class="grid gap-4 lg:grid-cols-2">
    <LaisCard
      v-for="group in partyGroups"
      :key="group.key"
      :title="group.label"
      prefix-icon="i-lucide-users"
    >
      <p v-if="!group.parties.length" class="text-sm text-lais-text-muted">
        {{ t('SALES.DETAIL.NO_PARTIES') }}
      </p>
      <div v-else class="grid gap-4">
        <div class="flex flex-wrap gap-2">
          <template
            v-for="kind in ['pessoa_fisica', 'pessoa_juridica']"
            :key="kind"
          >
            <span
              v-if="countLabel(group.parties, kind)"
              class="rounded-full bg-lais-bg-soft px-2 py-1 text-xs font-semibold text-lais-text-muted"
            >
              {{ countLabel(group.parties, kind) }}
            </span>
          </template>
        </div>
        <dl class="grid gap-3">
          <div
            v-for="party in group.parties"
            :key="party.id"
            class="grid gap-1 border-t border-lais-border-soft pt-3 first:border-t-0 first:pt-0 sm:grid-cols-2 sm:gap-4"
          >
            <div>
              <dt
                class="text-xs font-bold uppercase tracking-wide text-lais-text-muted"
              >
                {{ t('SALES.DETAIL.SALE_SUMMARY.NAME') }}
              </dt>
              <dd class="mt-1 text-sm text-lais-text-body">
                {{ party.display_name }}
              </dd>
            </div>
            <div>
              <dt
                class="text-xs font-bold uppercase tracking-wide text-lais-text-muted"
              >
                {{ t('SALES.DETAIL.SALE_SUMMARY.CONTACT') }}
              </dt>
              <dd class="mt-1 text-sm text-lais-text-body">
                {{ contact(party) || t('SALES.DETAIL.EMPTY_VALUE') }}
              </dd>
            </div>
          </div>
        </dl>
      </div>
    </LaisCard>
  </div>
</template>
